import { apiQuery, atsApiQuery } from '..';
import { transformApprovalTasks } from './transformApprovalTasks';

export const dashboardNormalApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      // Returns every task count in one call:
      // { scorecard, job_approval, offer_approval, survey,
      //   requisition_approval, requisition_approval_grouped }
      // `requisition_approval_grouped` collapses requisitions raised under one
      // headcount into a single row, which is what the Tasks page lists — the
      // raw `requisition_approval` stays behind the sidebar badge.
      // Optional `{ params: { filter_by } }` narrows `scorecard` and the unified
      // `approval` count to the Tasks page filters; `{}` stays the unfiltered
      // entry the sidebar shares.
      getTasksMetrics: builder.query({
        transformResponse: (response) => response.data,
        providesTags: ['GetTasks'],
        query: (args) => {
          return {
            method: 'get',
            url: `/tasks/metrics`,
            params: args?.params,
          };
        },
      }),
      // These five are reads, and they used to be declared as `builder.mutation`
      // purely because the call sites trigger them imperatively. That cost the
      // app every benefit RTK Query exists for: mutations are never cached,
      // never deduplicated and cannot carry `providesTags`, so switching tabs on
      // the Tasks page refetched all five lists every time.
      //
      // They are queries now, consumed through the generated `useLazyXQuery`
      // hooks so the imperative call shape at the call sites is unchanged.
      // `GetTasks` is an existing tag on this base (src/store/query/index.js),
      // so task-completing mutations can invalidate them.
      getScorecardTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/tasks/scorecards`,
            params,
          };
        },
      }),
      getJobApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'job', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      getOfferApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'offer', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      // Every approval type in one server-paginated list (no `type` param);
      // headcount-grouped requisitions arrive already collapsed to one row.
      getApprovalTasks: builder.query({
        transformResponse: transformApprovalTasks,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params,
          };
        },
      }),
      getSurveyTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { ...params };
          return {
            method: 'get',
            url: `/tasks/surveys`,
            params: newParams,
          };
        },
      }),
      getRequisitionApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'requisition', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      getSurveyFormTemplate: builder.mutation({
        transformResponse: (response) => response,
        query: ({ surveyId }) => {
          return {
            method: 'get',
            url: `/api_ats/v1/survey_form_templates/${surveyId}`,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const dashboardApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getApplicationMetrics: builder.mutation({
        transformResponse: (response) => response,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/metrics`,
            params,
          };
        },
      }),

      getDashboardActivitiesMetrics: builder.mutation({
        transformResponse: (response) => response,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/dashboard/metrics`,
            params,
          };
        },
      }),
      getDashboardActivities: builder.mutation({
        transformResponse: (response) => response,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/dashboard`,
            params,
          };
        },
      }),
      getUpcomingInterviews: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_interviews`,
            params,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useGetApplicationMetricsMutation,
  useGetDashboardActivitiesMetricsMutation,
  useGetDashboardActivitiesMutation,
  useGetUpcomingInterviewsMutation,
} = dashboardApi;

export const {
  useGetTasksMetricsQuery,
  useLazyGetTasksMetricsQuery,
  // Lazy variants: the call sites fetch imperatively, but these are cached,
  // deduplicated query entries rather than fire-and-forget mutations.
  useLazyGetScorecardTasksQuery,
  useLazyGetJobApprovalTasksQuery,
  useLazyGetOfferApprovalTasksQuery,
  useLazyGetApprovalTasksQuery,
  useLazyGetSurveyTasksQuery,
  useLazyGetRequisitionApprovalTasksQuery,
  useGetSurveyFormTemplateMutation,
} = dashboardNormalApi;
